import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { TrackJS } from 'trackjs';
import { State } from 'store/state';
import { searchListPoisDictionarySelector } from 'store/state/domainData/selectors';
import { userProfileSelector } from 'store/state/domainData/selectors/currentUser';
import { useSendEvent } from 'analytics/useSendEvent';
import {
  SEARCH_CARD_EXPOSE_VISIBILITY_RATIO,
  useSingletonSearchCardExposeObserver,
} from 'hooks/useSingletonIntersectionObserver';
import {
  buildSearchPropertyCardPayload,
  SearchCardPoi,
  SEARCH_EVENT_CATEGORY,
  SEARCH_PROPERTY_CARD_CLICK_EVENT,
  SEARCH_PROPERTY_CARD_EXPOSE_EVENT,
} from 'screens/List/searchPropertyCardEvents';

const EXPOSE_DWELL_MS = 1000;

interface SearchCardPosition {
  listIndex: number;
  page?: number;
}

export const useSearchPropertyCardEvents = <T extends HTMLElement>(
  containerRef: React.MutableRefObject<T>,
  poiId: string,
  position: SearchCardPosition
) => {
  const sendEvent = useSendEvent();
  const poi: SearchCardPoi = useSelector((state: State) => searchListPoisDictionarySelector(state)[poiId]);
  const currentUser = useSelector(userProfileSelector);

  const latestRef = useRef({ poi, currentUser, position });
  latestRef.current = { poi, currentUser, position };

  const hasExposedRef = useRef(false);
  const dwellTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Analytics must never break the card: poiEventPayload dereferences poc/locationPoint unguarded (see makeMapStateToPropsFactory)
  const sendCardEvent = (eventName: string) => {
    try {
      const latest = latestRef.current;
      const payload = buildSearchPropertyCardPayload({
        poi: latest.poi,
        currentUser: latest.currentUser,
        listIndex: latest.position.listIndex,
        page: latest.position.page,
      });
      if (payload) sendEvent(eventName, SEARCH_EVENT_CATEGORY, payload);
    }
    catch (e) {
      TrackJS.track([ `${eventName} payload build failed`, e ]);
    }
  };

  const clearDwellTimer = () => {
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
  };

  // isIntersecting is threshold-independent (true for any overlap), so the ratio is the only reliable 50% check
  useSingletonSearchCardExposeObserver((entry) => {
    if (entry.intersectionRatio < SEARCH_CARD_EXPOSE_VISIBILITY_RATIO) {
      clearDwellTimer();
      return;
    }
    if (hasExposedRef.current || dwellTimerRef.current) return;

    dwellTimerRef.current = setTimeout(() => {
      dwellTimerRef.current = null;
      hasExposedRef.current = true;
      sendCardEvent(SEARCH_PROPERTY_CARD_EXPOSE_EVENT);
    }, EXPOSE_DWELL_MS);
  }, containerRef);

  // A card instance can survive a page change (same poi on both pages after SSR hydration); each results page is a new view
  useEffect(() => {
    hasExposedRef.current = false;
    clearDwellTimer();
  }, [ poiId, position.page ]);

  useEffect(() => clearDwellTimer, []);

  const onCardClick = useCallback(() => sendCardEvent(SEARCH_PROPERTY_CARD_CLICK_EVENT), [ sendEvent ]);

  return { onCardClick };
};
